import { useEffect, useState } from 'react'
import { useCreateIncidentContext, useUserLanguage } from '@/customHooks'
import { Col, Row } from 'react-grid-system'
import styled from 'styled-components'
import textsData from '@/textData/CreateIncidentTexts.json'
import { capitalizeFirstLetter, incidentCategoryMapper, incidentTypeMapper, priorityMapper } from '@/helpers'
import { advancedNormalizeDate } from '@/helpers/normalizeDate'

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  padding: 2rem 3rem;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
`

const Label = styled.label`
  font-size: 15px;
  font-weight: 600;
  padding: 0px 50px 0 5px;
  color: ${({ theme }) => theme.colors.text};
  line-height: normal;
`;

const Input = styled.input`
  background: ${({ theme }) => theme.colors.background2};
  border: none;
  border-radius: ${({ theme }) => theme.border.radius.md};
  padding: 14px 20px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: #212121;
  width: 100%;
  box-sizing: border-box;
`;

export const CreateIncidentConfirmationDialog = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.confirmationDialog : textsData.en.confirmationDialog

  const { state, supplier, property, pictures } = useCreateIncidentContext()

  const [previewUrls, setPreviewUrls] = useState<string[]>([])

  useEffect(() => {
    if (pictures.length === 0) {
      setPreviewUrls([]);
      return;
    }

    const newUrls = pictures.map((picture) => URL.createObjectURL(picture));
    setPreviewUrls(newUrls);

    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [pictures])
  
  return (
    <Wrapper>
      <Row align='center'>
        <Col >
          <Label htmlFor="subject">{texts.form.subject}</Label>
        </Col>
        <Col md={7}>
          <Input id="subject" readOnly value={capitalizeFirstLetter(state.subject)} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="property">{texts.form.propertyName}</Label>
        </Col>
        <Col md={7}>
          <Input id="property" readOnly value={capitalizeFirstLetter(property?.name || '')} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col>
          <Label htmlFor="unity">{texts.form.unityName}</Label>
        </Col>
        <Col md={7}>
          <Input id="unity" readOnly value={property?.unities.find((unity) => unity.id === state.unityId)?.name ?? texts.unityNotSelected} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="address">{texts.form.address}</Label>
        </Col>
        <Col md={7}>
          <Input 
            id="address" 
            readOnly 
            value={(() => {
              const city = property?.address.city?.name?.trim() || ''
              const state = property?.address.state?.name?.trim() || ''
              if (city && state) return `${city}, ${state}`
              if (city) return city
              if (state) return state
              return ''
            })()} 
          />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col>
          <Label htmlFor="priority">{texts.form.priority}</Label>
        </Col>
        <Col md={7}>
          <Input id="priority" readOnly value={priorityMapper(state.incidentPriority, userLanguage)} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col>
          <Label htmlFor="incidentType">{texts.form.type}</Label>
        </Col>
        <Col md={7}>
          <Input id="incidentType" readOnly value={incidentTypeMapper(state.incidentType, userLanguage)} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col>
          <Label htmlFor="category">{texts.form.category}</Label>
        </Col>
        <Col md={7}>
          <Input id="category" readOnly value={incidentCategoryMapper(state.incidentCategory, userLanguage)} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="supplier">{texts.form.supplierName}</Label>
        </Col>
        <Col md={7}>
          <Input id="supplier" readOnly value={supplier ? capitalizeFirstLetter(`${supplier?.firstName} ${supplier?.lastName}`) : 'No se seleccionó Proveedor'} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="startDate">{texts.form.startDate}</Label>
        </Col>
        <Col md={7}>
          <Input id="startDate" readOnly value={advancedNormalizeDate(userLanguage, new Date(state.startDate).toISOString())} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="endDate">{texts.form.endDate}</Label>
        </Col>
        <Col md={7}>
          <Input id="endDate" readOnly value={advancedNormalizeDate(userLanguage, new Date(state.endDate).toISOString())} />
        </Col>
      </Row>
      <br />
      <Row align='center'>
        <Col >
          <Label htmlFor="picture">{texts.form.picture}</Label>
        </Col>
        <Col md={7}>
          {
            previewUrls.length > 0
            ? (
              previewUrls.map((previewUrl) => (
                <img src={previewUrl ?? ''} width="90" height="auto" alt="" />
              ))
            )
            : null
          }
        </Col>
      </Row>
      <br />
    </Wrapper>
  )
}